import React, { memo, useContext } from 'react';

import { AppContext } from '@prisjakt/ui-app-context';

import PencilIcon from 'mdi-react/PencilIcon';
import { StarIcon } from '@prisjakt/ui-icons';
import UserReviewPrompt from '../../areas/reviews/components/prompt';
import Rating from './components/rating';
import RatingIcon from '../rating/rating-icon';
import AboutLink from './components/about-link';

import * as S from './styles';

import { SmileyType } from '../rating/types';
import { BelongsTo, Store, Review, Product } from '../../areas/reviews/types';

const SCALE = 5;

type Props = {
  className?: string;
  rating?: number | null;
  count?: number | null;
  countTotal?: number | null;
  distribution?: number[] | null;
  belongsTo?: BelongsTo;
  showPrompt: boolean;
  store?: Store;
  product?: Product;
  myReview?: Review;
};

const ReviewsSummary = ({
  className,
  rating,
  count,
  countTotal,
  distribution,
  belongsTo,
  showPrompt = true,
  store,
  myReview,
}: Props) => {
  const { t } = useContext(AppContext);

  const title =
    belongsTo?.type === 'product'
      ? t('reviews', 'summary.product-title')
      : t('reviews', 'summary.store-title');

  const renderEmoticon = (rate: number) => {
    switch (rate) {
      case 1:
        return (
          <RatingIcon type={SmileyType.ANGRY} disabled={false} size={24} />
        );
      case 2:
        return (
          <RatingIcon
            type={SmileyType.DISSATISFIED}
            disabled={false}
            size={24}
          />
        );
      case 3:
        return (
          <RatingIcon type={SmileyType.NEUTRAL} disabled={false} size={24} />
        );
      case 4:
        return (
          <RatingIcon type={SmileyType.SMILING} disabled={false} size={24} />
        );
      case 5:
        return (
          <RatingIcon type={SmileyType.LAUGHING} disabled={false} size={24} />
        );
      default:
        return null;
    }
  };

  return (
    <S.SummaryWrapper className={className} data-test="ReviewsSummary">
      <S.Title>{title}</S.Title>

      <S.Summary>
        <Rating
          type={belongsTo?.type}
          rating={rating}
          count={count}
          countTotal={countTotal}
          name={belongsTo?.name}
        />
      </S.Summary>

      {belongsTo && showPrompt && (
        <UserReviewPrompt
          firstToReview={!count && !countTotal}
          belongsTo={belongsTo}
          store={store}
          currentReview={myReview}
          control={
            <S.PromptButton
              color="primary"
              iconPosition="left"
              icon={<PencilIcon size={18} />}
              data-test="WriteReviewButton"
            >
              {t('reviews', 'button.write-review')}
            </S.PromptButton>
          }
        />
      )}

      {distribution && countTotal && count && count >= SCALE ? (
        <>
          {belongsTo?.type === 'store' && (
            <div>{t('reviews', 'summary.store-distribution')}</div>
          )}

          {belongsTo?.type === 'product' && (
            <div>{t('reviews', 'summary.product-distribution')}</div>
          )}

          <S.Distribution>
            {distribution.map((ratingCount, index) => {
              const rate = SCALE - index;
              const ratio = Math.round((ratingCount / count) * 100);

              return (
                <React.Fragment key={index.toString()}>
                  <S.MeterLabel>
                    {belongsTo?.type === 'store' ? (
                      renderEmoticon(rate)
                    ) : (
                      <>
                        <S.StarRate>{rate}</S.StarRate>{' '}
                        <StarIcon height={12} styling="active" />
                      </>
                    )}
                  </S.MeterLabel>
                  <S.Meter>
                    <S.Indicator width={`${ratio}%`} />
                  </S.Meter>
                  <S.MeterCount>({ratingCount})</S.MeterCount>
                </React.Fragment>
              );
            })}
          </S.Distribution>
        </>
      ) : null}
      <AboutLink />
    </S.SummaryWrapper>
  );
};

export default memo(ReviewsSummary);
